import { DrawerContent, DrawerHeader, DrawerRoot, DrawerTitle, Skeleton } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStrategistCellDialogs } from '@/entities/strategist';
import type { StrategistCellDialogsRequest } from '@/entities/strategist';
import { formatLiftPp, formatTargetRate, liftColorClass } from '@/shared/lib/utils';
import { StatTile } from '@/shared/ui';
import { useDialogsDrawerStore } from '../model/store';
import { DialogRow } from './DialogRow';

/** Сколько диалогов запрашивать для среза. */
const DIALOGS_LIMIT = 50;

/**
 * Панель диалогов показанного среза раздела «Стратег»: показатели среза и его диалоги с
 * репликами. Открывается проваливанием из кластера, ячейки матрицы или перехода — адрес
 * среза и отбор страницы приходят через {@link useDialogsDrawerStore}.
 *
 * Монтируется по одному разу на каждой странице раздела.
 */
export const DialogsDrawerContent: FC = () => {
  const { t } = useTranslation(['common', 'strategist']);
  const spec = useDialogsDrawerStore((store) => store.spec);
  const close = useDialogsDrawerStore((store) => store.close);

  const body = useMemo<StrategistCellDialogsRequest>(
    () => ({
      objectionId: spec?.objectionId,
      argumentId: spec?.argumentId,
      conceptId: spec?.conceptId,
      block: spec?.block,
      objectionThreshold: spec?.objectionThreshold,
      argumentThreshold: spec?.argumentThreshold,
      conceptThreshold: spec?.conceptThreshold,
      transition: spec?.transition,
      filters: spec?.scope.filters ?? [],
      dateFrom: spec?.scope.dateFrom,
      dateTo: spec?.scope.dateTo,
      targetAttribute: spec?.scope.target,
      limit: DIALOGS_LIMIT,
    }),
    [spec],
  );

  const { data, isLoading } = useStrategistCellDialogs(body);

  const handleOpenChange = (open: boolean) => {
    if (!open) close();
  };

  return (
    <DrawerRoot open={spec !== null} onOpenChange={handleOpenChange}>
      <DrawerContent className="m-4 ml-0 rounded-2xl">
        <DrawerHeader>
          <DrawerTitle>{spec?.title ?? ''}</DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-col gap-4 overflow-auto p-6 pt-0">
          {isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-20 w-full" />
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-14 w-full" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <StatTile label={t('strategist:dialogsDrawer.influence.targetRate')}>
                  {formatTargetRate(data?.targetRate)}
                </StatTile>
                <StatTile
                  label={t('strategist:dialogsDrawer.influence.lift')}
                  valueClassName={liftColorClass(data?.lift ?? null)}
                >
                  {`${formatLiftPp(data?.lift ?? null)} ${t('strategist:dialogsDrawer.influence.pp')}`}
                </StatTile>
                <StatTile label={t('strategist:dialogsDrawer.influence.nDialogs')}>{data?.nDialogs ?? 0}</StatTile>
              </div>

              {data && data.dialogs.length > 0 ? (
                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium text-foreground">{t('strategist:dialogsDrawer.dialogs')}</span>
                  {data.dialogs.map((dialog, index) => (
                    <DialogRow key={`${dialog.cid}-${index}`} dialog={dialog} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('common:emptyTable')}</p>
              )}
            </>
          )}
        </div>
      </DrawerContent>
    </DrawerRoot>
  );
};
